import { useState, type ComponentType } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CallIcon, CheckIcon, ChevronIcon, CloseIcon, Drawer, FlowsIcon, InstagramIcon, LinkIcon, PlanIcon, PlusIcon, RedditIcon, relativeTime, Segments, Select, TelegramIcon, TrashIcon, useConfirm, useToast, XIcon } from "@signet/ui";
import { api, CreatorAvatar, NowPanelHead, useCreatorPhotos, useMe, usePersonName } from "@signet/web-kit";
import { CLink, creatorHref } from "./shared.tsx";
import { isComplete, progressOf, TRACKER_STEPS, type Tracker, type TrackerCreator, type TrackerStep } from "../shared/onboarding-tracker.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Ben's onboarding tracker on Now (his whiteboard and mockup, 2026-11-02):
   new creators down the side, his eight steps across in his order, one tick
   each, a progress bar. All, In progress and Complete, so a finished creator
   can still be looked back on. A creator's name opens the side panel: who
   ticked what and when, the plan, notes, and their main IG accounts, which
   go into Main Accounts. Every tick is by hand (Jay, 2026-11-04).
   ────────────────────────────────────────────────────────────────────────── */

const BASE = "/api/m/creators/onboarding-tracker";
const KEY = ["creators", "onboarding-tracker"] as const;

type Tab = "all" | "progress" | "complete";

const fetchTracker = () => api<Tracker>(BASE);

function useTrackerWrite() {
  const client = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (call: { path: string; method: string; body?: unknown }) => api<Tracker>(`${BASE}${call.path}`, { method: call.method, body: call.body }),
    onSuccess: (data) => client.setQueryData(KEY, data),
    onError: (error) => toast.error("Couldn't save", error instanceof Error ? error.message : undefined),
  });
}

const enc = encodeURIComponent;

export function OnboardingTrackerPanel() {
  const tracker = useQuery({ queryKey: KEY, queryFn: fetchTracker, refetchInterval: 60_000 });
  const photos = useCreatorPhotos(BASE);
  const write = useTrackerWrite();
  const [tab, setTab] = useState<Tab>("progress");
  const [open, setOpen] = useState<string | null>(null);

  const all = tracker.data?.creators ?? [];
  const done = all.filter(isComplete);
  const going = all.filter((c) => !isComplete(c));
  const rows = tab === "all" ? all : tab === "complete" ? done : going;
  const opened = all.find((c) => c.creator === open) ?? null;

  const tick = (creator: string, step: TrackerStep, on: boolean) => write.mutate({ path: `/${enc(creator)}/ticks/${step}`, method: "PUT", body: { done: on } });

  return (
    <section className="panel cr-ot">
      <NowPanelHead
        label="Onboarding"
        title="New creators"
        sub={tracker.data ? `${going.length} in progress, ${done.length} complete` : " "}
        side={
          <span className="cr-ot-head-side">
            <Segments
              label="Which creators"
              value={tab}
              options={[
                { value: "all", label: `All (${all.length})` },
                { value: "progress", label: `In progress (${going.length})` },
                { value: "complete", label: `Complete (${done.length})` },
              ]}
              onChange={(next) => setTab(next as Tab)}
            />
            {tracker.data?.available.length ? (
              <span className="cr-ot-add-creator">
              <Select
                value=""
                compact
                searchable
                label="Add a creator"
                placeholder="Add a creator"
                options={tracker.data.available.map((name) => ({ value: name, label: name, image: photos(name) ?? undefined }))}
                onChange={(name) => name && write.mutate({ path: "", method: "POST", body: { creator: name } })}
              />
              </span>
            ) : null}
          </span>
        }
      />
      <div className="panel-body flush">
        {tracker.isError ? <p className="faint cr-ot-empty">Couldn't load the onboarding tracker.</p> : null}
        {tracker.data && !rows.length ? (
          <p className="cr-ot-empty">
            <CheckIcon size={15} /> {tab === "complete" ? "Nobody has finished onboarding yet." : tab === "progress" ? "Nobody is being onboarded right now." : "No creators on the tracker."}
          </p>
        ) : null}
        {rows.length ? (
          <div className="table-scroll">
            <table className="cr-ot-table">
              <thead>
                <tr>
                  <th scope="col" className="cr-ot-who">Creator</th>
                  {TRACKER_STEPS.map((s) => (
                    <th scope="col" key={s.id}>
                      <span className="cr-ot-colhead">
                        <StepIcon step={s.id} size={13} />
                        {s.label}
                      </span>
                    </th>
                  ))}
                  <th scope="col" className="cr-ot-progress-col">Progress</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.creator} className={open === c.creator ? "selected" : undefined}>
                    <th scope="row" className="cr-ot-who">
                      <button type="button" className="cr-ot-name" onClick={() => setOpen(c.creator)} aria-label={`Open ${c.creator}`}>
                        <CreatorAvatar name={c.creator} src={photos(c.creator)} size={30} />
                        <span>{c.creator}</span>
                      </button>
                    </th>
                    {TRACKER_STEPS.map((s) => {
                      const on = Boolean(c.ticks[s.id]);
                      return (
                        <td key={s.id}>
                          <button
                            type="button"
                            className={`cr-ot-tick${on ? " on" : ""}`}
                            aria-pressed={on}
                            aria-label={`${s.label} for ${c.creator}`}
                            onClick={() => tick(c.creator, s.id, !on)}
                          >
                            {on ? <CheckIcon size={14} /> : null}
                          </button>
                        </td>
                      );
                    })}
                    <td className="cr-ot-progress-col">
                      <Progress value={progressOf(c)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
      {opened ? <TrackerDrawer creator={opened} photo={photos(opened.creator)} onClose={() => setOpen(null)} onTick={tick} /> : null}
    </section>
  );
}

function Progress({ value }: { value: number }) {
  return (
    <span className="cr-ot-progress">
      <span className="cr-ot-bar" aria-hidden="true">
        <span style={{ width: `${value}%` }} />
      </span>
      <span className="tabular">{value}%</span>
    </span>
  );
}

const STEP_ICONS: Record<TrackerStep, ComponentType<{ size?: number }>> = {
  telegram: TelegramIcon,
  links: LinkIcon,
  call: CallIcon,
  plan: PlanIcon,
  flows: FlowsIcon,
  ig: InstagramIcon,
  twitter: XIcon,
  reddit: RedditIcon,
};

export function StepIcon({ step, size = 15 }: { step: TrackerStep; size?: number }) {
  const Icon = STEP_ICONS[step];
  return <Icon size={size} />;
}

function TrackerDrawer({ creator, photo, onClose, onTick }: { creator: TrackerCreator; photo: string | null; onClose: () => void; onTick: (creator: string, step: TrackerStep, on: boolean) => void }) {
  const write = useTrackerWrite();
  const { confirm, element } = useConfirm();
  const ticked = TRACKER_STEPS.filter((s) => creator.ticks[s.id]).length;
  const complete = isComplete(creator);
  const next = TRACKER_STEPS.find((s) => !creator.ticks[s.id]);
  // The first unfinished step starts open, so the next thing to do is in view.
  const [open, setOpen] = useState<Set<TrackerStep>>(() => new Set(next ? [next.id] : []));
  const toggle = (step: TrackerStep) =>
    setOpen((current) => {
      const nextSet = new Set(current);
      if (nextSet.has(step)) nextSet.delete(step);
      else nextSet.add(step);
      return nextSet;
    });

  const remove = async () => {
    const ok = await confirm({
      title: `Take ${creator.creator} off the tracker?`,
      body: "Their ticks, plan and notes are kept, and come back if they are added again.",
      confirmLabel: "Take off",
      danger: true,
    });
    if (!ok) return;
    write.mutate({ path: `/${enc(creator.creator)}`, method: "DELETE" }, { onSuccess: onClose });
  };

  return (
    <Drawer
      open
      narrow
      onClose={onClose}
      title={
        <span className="cr-ot-drawer-title">
          <CreatorAvatar name={creator.creator} src={photo} size={28} />
          {creator.creator}
        </span>
      }
      subtitle={`On the tracker since ${new Date(creator.addedAt).toLocaleDateString(undefined, { day: "numeric", month: "short" })}`}
      head={<span className={`cr-ot-status${complete ? " done" : ""}`}>{complete ? "Complete" : "In progress"}</span>}
      footer={
        <>
          <button type="button" className="text-button tiny danger cr-ot-remove" onClick={() => void remove()}>
            <TrashIcon size={13} /> Take off the tracker
          </button>
          <span className="spacer" />
          <CLink to={creatorHref(creator.creator)} className="text-button tiny accent">
            Open creator page
          </CLink>
        </>
      }
    >
      {element}
      <div className="cr-ot-drawer">
        <section className="cr-ot-summary" aria-label="Progress">
          <div className="cr-ot-summary-top">
            <span className="cr-ot-summary-figure tabular">
              {ticked}
              <small>of {TRACKER_STEPS.length} steps</small>
            </span>
            <span className="cr-ot-next">{next ? <>Next: <strong>{next.label}</strong></> : "Every step is done"}</span>
          </div>
          <span className="cr-ot-segments" aria-hidden="true">
            {TRACKER_STEPS.map((s) => (
              <i key={s.id} className={creator.ticks[s.id] ? "on" : undefined} title={s.label} />
            ))}
          </span>
        </section>

        <ul className="cr-ot-steps" aria-label="Steps">
          {TRACKER_STEPS.map((s) => (
            <StepRow
              key={s.id}
              creator={creator}
              step={s.id}
              label={s.label}
              expanded={open.has(s.id)}
              onToggle={() => toggle(s.id)}
              onTick={(on) => onTick(creator.creator, s.id, on)}
            />
          ))}
        </ul>

        <Writing key={`plan-${creator.creator}`} creator={creator.creator} field="plan" label="Plan" value={creator.plan} placeholder="What we're doing with them: platforms, content, flows." />
        <Writing key={`notes-${creator.creator}`} creator={creator.creator} field="notes" label="Notes" value={creator.notes} placeholder="Anything else worth keeping." />

        <MainAccounts creator={creator.creator} handles={creator.mainAccounts} />
      </div>
    </Drawer>
  );
}

function StepRow({ creator, step, label, expanded, onToggle, onTick }: { creator: TrackerCreator; step: TrackerStep; label: string; expanded: boolean; onToggle: () => void; onTick: (on: boolean) => void }) {
  const name = usePersonName();
  const write = useTrackerWrite();
  const [draft, setDraft] = useState("");
  const tick = creator.ticks[step];
  const lines = creator.subitems.filter((x) => x.step === step);
  const doneLines = lines.filter((x) => x.done).length;
  const panelId = `cr-ot-sub-${step}`;

  const add = () => {
    const text = draft.trim();
    if (!text) return;
    write.mutate({ path: `/${enc(creator.creator)}/subitems`, method: "POST", body: { step, label: text } }, { onSuccess: () => setDraft("") });
  };

  return (
    <li className={`cr-ot-steprow${tick ? " done" : ""}${expanded ? " open" : ""}`}>
      <div className="cr-ot-stephead">
        <button type="button" className={`cr-ot-tick${tick ? " on" : ""}`} aria-pressed={Boolean(tick)} aria-label={`${label} done`} onClick={() => onTick(!tick)}>
          {tick ? <CheckIcon size={13} /> : null}
        </button>
        <button type="button" className="cr-ot-stepbutton" aria-expanded={expanded} aria-controls={panelId} onClick={onToggle}>
          <span className="cr-ot-stepicon" aria-hidden="true">
            <StepIcon step={step} />
          </span>
          <span className="cr-ot-steplabel">{label}</span>
          {lines.length ? <span className="cr-ot-count tabular">{doneLines}/{lines.length}</span> : null}
          <span className="cr-ot-when">{tick ? `${name(tick.by)}, ${relativeTime(tick.at)}` : ""}</span>
          <ChevronIcon size={14} direction={expanded ? "up" : "down"} className="cr-ot-chevron" />
        </button>
      </div>
      <div className="cr-ot-subwrap" id={panelId} hidden={!expanded}>
        <ul className="cr-ot-subs">
          {lines.map((x) => (
            <li key={x.id} className={x.done ? "done" : undefined}>
              <button
                type="button"
                className={`cr-ot-box${x.done ? " on" : ""}`}
                aria-pressed={Boolean(x.done)}
                aria-label={x.label}
                onClick={() => write.mutate({ path: `/${enc(creator.creator)}/subitems/${enc(x.id)}`, method: "PUT", body: { done: !x.done } })}
              >
                {x.done ? <CheckIcon size={11} /> : null}
              </button>
              <span className="cr-ot-sublabel">{x.label}</span>
              {x.done ? <span className="cr-ot-subwhen">{name(x.done.by)}</span> : null}
              <button
                type="button"
                className="icon-button ghost small cr-ot-subremove"
                aria-label={`Remove "${x.label}"`}
                onClick={() => write.mutate({ path: `/${enc(creator.creator)}/subitems/${enc(x.id)}`, method: "DELETE" })}
              >
                <CloseIcon size={12} />
              </button>
            </li>
          ))}
          <li className="cr-ot-subadd">
            <PlusIcon size={13} aria-hidden="true" />
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
              placeholder="Add a line"
              aria-label={`Add a line under ${label}`}
              maxLength={200}
            />
          </li>
        </ul>
      </div>
    </li>
  );
}

function Writing({ creator, field, label, value, placeholder }: { creator: string; field: "plan" | "notes"; label: string; value: string; placeholder: string }) {
  const write = useTrackerWrite();
  const toast = useToast();
  const [text, setText] = useState(value);
  const [base, setBase] = useState(value);
  // A save from elsewhere replaces the box only while nothing unsaved is in it.
  if (value !== base) {
    if (text === base) setText(value);
    setBase(value);
  }
  const changed = text !== value;
  const save = () => write.mutate({ path: `/${enc(creator)}`, method: "PUT", body: { [field]: text } }, { onSuccess: () => toast.success("Saved", `${label} for ${creator}.`) });
  return (
    <section className="cr-ot-block">
      <h3 className="cr-ot-section-title">{label}</h3>
      <textarea
        className="cr-ot-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && changed) {
            e.preventDefault();
            save();
          }
        }}
        placeholder={placeholder}
        rows={field === "plan" ? 5 : 3}
        aria-label={`${label} for ${creator}`}
        maxLength={20_000}
      />
      {changed ? (
        <span className="cr-ot-save-row">
          <button type="button" className="secondary-button compact" onClick={() => setText(value)}>Undo</button>
          <button type="button" className="button brass compact" disabled={write.isPending} onClick={save}>Save</button>
        </span>
      ) : null}
    </section>
  );
}

function MainAccounts({ creator, handles }: { creator: string; handles: string[] }) {
  const { can } = useMe();
  const client = useQueryClient();
  const toast = useToast();
  const [handle, setHandle] = useState("");
  const add = useMutation({
    mutationFn: () => api<{ handle: string; banned: boolean }>("/api/m/mainaccounts/accounts", { body: { creator, handle } }),
    onSuccess: (r) => {
      setHandle("");
      toast.success("Added to Main Accounts", r.banned ? `@${r.handle} can't be found on Instagram, so it is marked Banned.` : `@${r.handle} is read daily from now.`);
      void client.invalidateQueries({ queryKey: KEY });
      void client.invalidateQueries({ queryKey: ["mainaccounts"] });
    },
    onError: (error) => toast.error("Couldn't add it", error instanceof Error ? error.message : undefined),
  });
  return (
    <section className="cr-ot-block">
      <h3 className="cr-ot-section-title">
        Main IG accounts {handles.length ? <span className="faint tabular">{handles.length}</span> : null}
      </h3>
      {handles.length ? (
        <ul className="cr-ot-handles">
          {handles.map((h) => (
            <li key={h}>
              <a className="cr-ot-handle" href={`https://www.instagram.com/${h}/`} target="_blank" rel="noreferrer noopener">
                <InstagramIcon size={13} />@{h}
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="faint cr-ot-none">None in Main Accounts yet.</p>
      )}
      {can("mainaccounts.account:edit") ? (
        <form
          className="cr-ot-add"
          onSubmit={(e) => {
            e.preventDefault();
            if (handle.trim()) add.mutate();
          }}
        >
          <span className="cr-ot-add-field">
            <InstagramIcon size={14} aria-hidden="true" />
            <input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="Handle or profile link" aria-label={`Add a main IG account for ${creator}`} />
          </span>
          <button type="submit" className="secondary-button compact" disabled={!handle.trim() || add.isPending}>
            <PlusIcon size={13} /> Add
          </button>
        </form>
      ) : null}
    </section>
  );
}
